/**
 * Cálculo de bandas de colores para una resistencia según su valor.
 * Solo devuelve bandas si el valor coincide EXACTAMENTE con una serie
 * E24 (5% tolerancia). En cualquier otro caso, devuelve null y la
 * resistencia se pinta sin bandas (solo con la etiqueta de texto).
 */

const E24 = [10, 11, 12, 13, 15, 16, 18, 20, 22, 24, 27, 30, 33, 36, 39, 43, 47, 51, 56, 62, 68, 75, 82, 91];

const DIGIT_COLORS = [
  '#111111', // 0 negro
  '#7a4a1a', // 1 marrón
  '#c00000', // 2 rojo
  '#e26a00', // 3 naranja
  '#e5c300', // 4 amarillo
  '#0aa64a', // 5 verde
  '#1a4fd0', // 6 azul
  '#7a1a99', // 7 violeta
  '#7f7f7f', // 8 gris
  '#f2f2f2', // 9 blanco
];

const TOLERANCE_GOLD = '#c8a24a';
const MULT_SILVER = '#c0c0c0';

/**
 * Parseo laxo de un valor de resistencia. Soporta:
 *   "1000", "220", "220R", "1k", "1K", "4.7k", "4k7", "1M", "2.2M", "2M2",
 *   "R47" (=0.47Ω), "R5" (=0.5Ω).
 * Devuelve el valor en ohmios (número) o null si no se puede parsear.
 */
export function parseResistanceValue(raw: string): number | null {
  if (!raw) return null;
  const s = raw.trim().replace(/\s+/g, '').replace(/Ω|ω|ohm/i, '').replace(/,/g, '.');
  if (!s) return null;

  // Letra como separador decimal: 4k7 → 4.7k, 2M2 → 2.2M, R47 → 0.47.
  const letterMap: Record<string, number> = { R: 1, r: 1, K: 1e3, k: 1e3, M: 1e6 };
  const m = s.match(/^([0-9.]*)([RrKkM])([0-9]+)$/);
  if (m) {
    const int = m[1] === '' ? '0' : m[1];
    const num = parseFloat(`${int}.${m[3]}`);
    if (!isFinite(num)) return null;
    return num * (letterMap[m[2]!] ?? 1);
  }

  // Sufijo unidad al final: "220R", "1k", "4.7M".
  const m2 = s.match(/^([0-9.]+)([RrKkM]?)$/);
  if (m2) {
    const num = parseFloat(m2[1]!);
    if (!isFinite(num)) return null;
    return num * (letterMap[m2[2] ?? ''] ?? 1);
  }
  return null;
}

/**
 * Devuelve 4 colores (banda1, banda2, multiplicador, tolerancia) si el
 * valor pertenece a la serie E24. Devuelve null si no coincide.
 */
export function computeResistorBands(valueStr: string | undefined): string[] | null {
  if (!valueStr) return null;
  const ohms = parseResistanceValue(valueStr);
  if (ohms === null || ohms <= 0) return null;

  // Normaliza mantisa a rango [10, 100).
  const log = Math.log10(ohms);
  const power = Math.floor(log) - 1;
  const mantissa = ohms / Math.pow(10, power);
  const mInt = Math.round(mantissa);

  // Exigimos coincidencia exacta (con tolerancia de coma flotante).
  if (Math.abs(mantissa - mInt) > 1e-6) return null;
  if (!E24.includes(mInt)) return null;

  const d1 = Math.floor(mInt / 10);
  const d2 = mInt % 10;
  const dig1 = DIGIT_COLORS[d1];
  const dig2 = DIGIT_COLORS[d2];
  if (!dig1 || !dig2) return null;

  let mult: string;
  if (power === -2) mult = MULT_SILVER;
  else if (power === -1) mult = TOLERANCE_GOLD;
  else if (power >= 0 && power <= 9) {
    const c = DIGIT_COLORS[power];
    if (!c) return null;
    mult = c;
  } else {
    return null;
  }

  return [dig1, dig2, mult, TOLERANCE_GOLD];
}
